// This file is generated by scripts/generators/generate-usecase-table-organisms.ts
import React from 'react'
import type { UsecaseTableBusinessRisks } from 'poc-ai-biz-irdb_domain-model'
import { UsecaseTable } from '../UsecaseTable'
import { UsecaseTableAccordion } from '../../UsecaseTableAccordion'

type Props = {
  contents: UsecaseTableBusinessRisks[]
  status: 'pending' | 'processing' | 'completed' | 'failed'
  defaultExpanded: boolean
}

export const UsecaseTableBusinessRisksOrganism: React.FC<Props> = ({ contents, status, defaultExpanded = false }) => {
  // contentsをfilerNameとfiscalYearでグルーピング
  const groupedByCompanyAndYear = contents.reduce(
    (acc, item) => {
      const companyKey = `${item.filerName || ''}_${item.secCode || ''}`
      if (!acc[companyKey]) {
        acc[companyKey] = {
          filerName: item.filerName || '',
          secCode: item.secCode || '',
          years: {},
        }
      }
      const yearKey = item.fiscalYearEndYear.toString()
      if (!acc[companyKey].years[yearKey]) {
        acc[companyKey].years[yearKey] = []
      }
      acc[companyKey].years[yearKey].push(item)
      return acc
    },
    {} as Record<
      string,
      {
        filerName: string
        secCode: string
        years: Record<string, UsecaseTableBusinessRisks[]>
      }
    >,
  )

  // 企業ごとに最新年度のデータを取得
  const companiesWithItems: Array<{
    filerName: string
    secCode: string
    fiscalYearEndYear: number
    items: UsecaseTableBusinessRisks[]
  }> = []
  Object.values(groupedByCompanyAndYear).forEach((company) => {
    const years = Object.keys(company.years).sort((a, b) => parseInt(b) - parseInt(a))
    const latestYear = years[0]
    if (!latestYear) {
      return
    }
    const items = company.years[latestYear]
    if (!items) {
      return
    }

    // appearanceIndexでソート
    items.sort((a, b) => (a.appearanceIndex || 0) - (b.appearanceIndex || 0))

    companiesWithItems.push({
      filerName: company.filerName,
      secCode: company.secCode,
      fiscalYearEndYear: parseInt(latestYear),
      items,
    })
  })

  const formatValue = (value: string | number | string[] | object | null | undefined) => {
    if (!value) return '-'
    if (Array.isArray(value)) {
      return (
        <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
          {value.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      )
    }
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2)
    }
    if (typeof value === 'number') {
      return value.toLocaleString()
    }
    return value
  }

  const formatNumber = (value: number | null | undefined, unit: string = '') => {
    if (!value) return '-'
    return `${value.toLocaleString()}${unit}`
  }

  // ローディング状態の表示
  if (status === 'pending' || status === 'processing') {
    return (
      <UsecaseTableAccordion summary="事業リスク" defaultExpanded={defaultExpanded} disabled={true}>
        <span className="text-gray-500">読み込み中...</span>
      </UsecaseTableAccordion>
    )
  }

  // エラー状態の表示
  if (status === 'failed') {
    return (
      <UsecaseTableAccordion summary="事業リスク" defaultExpanded={defaultExpanded} disabled={false}>
        <span className="text-red-600">データの読み込みに失敗しました</span>
      </UsecaseTableAccordion>
    )
  }

  return (
    <UsecaseTableAccordion summary="事業リスク" defaultExpanded={defaultExpanded} disabled={false}>
      <div className="flex flex-col gap-6">
        {companiesWithItems.map((companyData, companyIndex) => (
          <div key={companyIndex} className="flex flex-col gap-2">
            <h3 className="text-sm font-bold">{companyData.filerName}</h3>
            <UsecaseTable>
              <UsecaseTable.Head>
                <UsecaseTable.Row>
                  {(
                    [
                      { name: 'category', displayName: 'カテゴリー', type: 'string' },
                      { name: 'title', displayName: 'タイトル', type: 'string' },
                      { name: 'contentSummary', displayName: 'リスク概要', type: 'string' },
                    ] as const
                  ).map((col) => (
                    <UsecaseTable.Cell key={col.name} type="text" component="th">
                      {col.displayName}
                    </UsecaseTable.Cell>
                  ))}
                </UsecaseTable.Row>
              </UsecaseTable.Head>
              <UsecaseTable.Body>
                {companyData.items.map((item, rowIndex) => (
                  <UsecaseTable.Row key={rowIndex}>
                    {(
                      [
                        { name: 'category', displayName: 'カテゴリー', type: 'string' },
                        { name: 'title', displayName: 'タイトル', type: 'string' },
                        { name: 'contentSummary', displayName: 'リスク概要', type: 'string' },
                      ] as const
                    ).map((col) => {
                      const cellType = ['int', 'float'].includes(col.type) ? 'currency' : 'text'
                      const value = formatValue(item?.[col.name as keyof UsecaseTableBusinessRisks])

                      return (
                        <UsecaseTable.Cell key={col.name} type={cellType}>
                          {value}
                        </UsecaseTable.Cell>
                      )
                    })}
                  </UsecaseTable.Row>
                ))}
              </UsecaseTable.Body>
            </UsecaseTable>
          </div>
        ))}
      </div>
    </UsecaseTableAccordion>
  )
}
